import { useReportDetailStore } from "./store";
import { RelacoesSectionListItem, UpdateFnPromise } from "./interface";
import { ReportMetadata, ReportSection, SummaryData, SummaryStatus } from "~/types/global";
import { REPORT_DETAIL_INITIAL_STATE } from "./state";
import { REPORT_SECTIONS } from "~/helpers/constants";
import { getProfileImageFromSection } from "~/helpers";
import { calculateSectionListRecords } from "./utils";

const getStore = () => useReportDetailStore;

const calculateRelacoesSectionList = (sections: ReportSection[]): RelacoesSectionListItem[] => {
  const relacoesSectionList: RelacoesSectionListItem[] = [];
  const relacoesSection = sections.find(section => section.title === "Relações");
  if (relacoesSection && Array.isArray(relacoesSection.data)) {
    const ordinalLabels = [
      "primeiro vínculo",
      "segundo vínculo",
      "terceiro vínculo",
      "quarto vínculo",
      "quinto vínculo",
      "sexto vínculo",
      "sétimo vínculo",
      "oitavo vínculo",
      "nono vínculo",
      "décimo vínculo"
    ];

    relacoesSection.data.forEach((item, index) => {
      if (index < ordinalLabels.length) {
        relacoesSectionList.push({
          title: ordinalLabels[index],
          data_count: 1,
          index: index,
          interface_id: `relacoes-${index}-${relacoesSection.interface_id || 'fallback'}`
        });
      }
    });
  }
  return relacoesSectionList;
};

export const updateSectionListRecords = () => {
  const store = getStore().getState();
  const sections = store.data.sections;

  const { sectionListRecords, totalRecords } = calculateSectionListRecords(
    sections,
    store.data.reportType,
    store.data.selectedCombinedFilter
  );
  const relacoesSectionList = calculateRelacoesSectionList(sections);

  store.actions.setSectionListRecords(sectionListRecords);
  store.actions.setTotalRecords(totalRecords);
  store.actions.setRelacoesSectionList(relacoesSectionList);
};

export const setReportSections = (sections: ReportSection[]) => {
  const useReportDetailStore = getStore();
  const store = useReportDetailStore.getState();

  // Profile image comes from "Imagens" (legacy reports) or, if absent, "Perfil"
  // (redes_sociais) — getProfileImageFromSection distinguishes the two `detalhes` shapes.
  const imagensSection = sections.find(
    (section) => section.title === REPORT_SECTIONS.imagens
  ) ?? sections.find(
    (section) => section.title === REPORT_SECTIONS.rs_perfil
  );
  const profileImage = getProfileImageFromSection(imagensSection);

  const { sectionListRecords, totalRecords } = calculateSectionListRecords(
    sections,
    store.data.reportType,
    store.data.selectedCombinedFilter
  );
  const relacoesSectionList = calculateRelacoesSectionList(sections);

  store.actions.setReportSections(sections);
  store.actions.setProfileImage(profileImage);
  store.actions.setSectionListRecords(sectionListRecords);
  store.actions.setRelacoesSectionList(relacoesSectionList);
  store.actions.setTotalRecords(totalRecords);
};

export const setDeletedSections = (deletedSections: ReportSection[]) => {
  getStore().getState().actions.setDeletedSections(deletedSections);
};

export const setReportMetadata = (metadata: ReportMetadata) => {
  getStore().getState().actions.setMetadata(metadata);
};

export const setReportType = (reportType: string) => {
  getStore().getState().actions.setReportType(reportType);
};

export const setSelectedCombinedFilter = (filterId: string) => {
  getStore().getState().actions.setSelectedCombinedFilter(filterId);
};

export const setCombinedReportSources = (sources: Array<{
  report_type: string;
  report_input_value: string | Record<string, any>;
  user_reports_id: string;
  report_name: string;
}>) => {
  getStore().getState().actions.setCombinedReportSources(sources);
};

export const setProfileImage = (profileImage: string) => {
  getStore().getState().actions.setProfileImage(profileImage);
};

export const forceReload = () => {
  getStore().getState().actions.forceReload();
};

export const updateSectionEntries = (
  sectionTitle: string,
  updaterFn: UpdateFnPromise,
  testEntryDeletedFn: (entry: ReportSection["data"][0]) => boolean,
  testSectionDeletedFn: (section: ReportSection) => boolean,
  calculateDataCountFn?: (section: ReportSection) => number,
  includeSubsections?: boolean,
  crossSectionUpdate?: { matchingProp: string; updaterFn: UpdateFnPromise },
  targetIndex?: number
) => {
  getStore().getState().actions.updateSectionEntries(
    sectionTitle,
    updaterFn,
    testEntryDeletedFn,
    testSectionDeletedFn,
    calculateDataCountFn,
    includeSubsections,
    crossSectionUpdate,
    targetIndex
  );
};

export const updateSubsectionWithMainSection = (
  sectionTitle: string,
  subsectionName: string,
  matchingProp: string,
  updaterFn: UpdateFnPromise,
  testEntryDeletedFn: (entry: ReportSection["data"][0]) => boolean,
  testSectionDeletedFn: (section: ReportSection) => boolean,
  calculateDataCountFn?: (section: ReportSection) => number
) => {
  getStore().getState().actions.updateSubsectionWithMainSection(
    sectionTitle,
    subsectionName,
    matchingProp,
    updaterFn,
    testEntryDeletedFn,
    testSectionDeletedFn,
    calculateDataCountFn
  );
};

export const scheduleAutoSave = () => {
  getStore().getState().actions.scheduleAutoSave();
};

export const cancelAutoSave = () => {
  getStore().getState().actions.cancelAutoSave();
};

export const setAutoSaveMutation = (mutation: any) => {
  getStore().getState().actions.setAutoSaveMutation(mutation);
};

export const setAutoSaveEnabled = (enabled: boolean) => {
  getStore().getState().actions.setAutoSaveEnabled(enabled);
};

export const clearPendingChanges = () => {
  getStore().getState().actions.clearPendingChanges();
};

export const useActionLoading = () => {
  const useReportDetailStore = getStore();
  return useReportDetailStore((state) => state.ui.isActionLoading);
};

export const setActiveSection = (sectionKey: string | null) => {
  getStore().getState().actions.setActiveSection(sectionKey);
};

export const setScrollLoading = (loading: boolean) => {
  getStore().getState().actions.setScrollLoading(loading);
};

export const setAccordionToggle = (toggle: ((sectionTitle: string) => void) | null) => {
  getStore().getState().actions.setAccordionToggle(toggle);
};

export const getAccordionToggle = () => {
  return getStore().getState().ui.accordionToggle;
};

export const setPendingScrollTarget = (target: string | null) => {
  getStore().getState().actions.setPendingScrollTarget(target);
};

export const getPendingScrollTarget = () => {
  return getStore().getState().ui.pendingScrollTarget;
};

export const setOpenSections = (sections: string[]) => {
  getStore().getState().actions.setOpenSections(sections);
};

export const toggleOpenSection = (sectionId: string) => {
  getStore().getState().actions.toggleOpenSection(sectionId);
};

export const addOpenSection = (sectionId: string) => {
  getStore().getState().actions.addOpenSection(sectionId);
};

export const getOpenSections = () => {
  return getStore().getState().ui.openSections;
};

export const isSectionOpen = (sectionId: string) => {
  return getStore().getState().ui.openSections.includes(sectionId);
};

export const setOpenSectionsTrash = (sections: string[]) => {
  getStore().getState().actions.setOpenSectionsTrash(sections);
};

export const toggleOpenSectionTrash = (sectionId: string) => {
  getStore().getState().actions.toggleOpenSectionTrash(sectionId);
};

export const addOpenSectionTrash = (sectionId: string) => {
  getStore().getState().actions.addOpenSectionTrash(sectionId);
};

export const getOpenSectionsTrash = () => {
  return getStore().getState().ui.openSectionsTrash;
};

export const isSectionOpenTrash = (sectionId: string) => {
  return getStore().getState().ui.openSectionsTrash.includes(sectionId);
};

export const setActiveRelacaoTab = (tab: 'semactic-analysis' | 'relationship-flow') => {
  getStore().getState().actions.setActiveRelacaoTab(tab);
};

export const setHighlightedEntityId = (entityId: string | null) => {
  getStore().getState().actions.setHighlightedEntityId(entityId);
};

export const setHighlightedEntityAndActiveSection = (entityIndex: number | null) => {
  const store = getStore().getState();

  console.log('[setHighlightedEntityAndActiveSection] Called with:', { entityIndex });

  if (entityIndex === null) {
    store.actions.setHighlightedEntityId(null);
    console.log('[setHighlightedEntityAndActiveSection] Cleared highlight');
    return;
  }

  const entityCardId = `entity-card-${entityIndex}`;
  store.actions.setHighlightedEntityId(entityCardId);

  const relacoesList = store.ui.relacoesSectionList;
  const matchingRelation = relacoesList.find(item => item.index === entityIndex);

  console.log('[setHighlightedEntityAndActiveSection] Found matching relation:', {
    entityCardId,
    relacoesList,
    matchingRelation,
    willSetActiveSection: matchingRelation?.interface_id
  });

  if (matchingRelation?.interface_id) {
    store.actions.setActiveSection(matchingRelation.interface_id);
    console.log('[setHighlightedEntityAndActiveSection] Set active section to:', matchingRelation.interface_id);
  }
};

export const resetReportDetailStore = () => {
  getStore().setState({
    ...REPORT_DETAIL_INITIAL_STATE,
  });
};

export const resetRelacoesState = () => {
  const store = getStore().getState();
  store.actions.resetRelacoesState();
};

export const setRefetchReport = (refetch: (() => Promise<any>) | null) => {
  getStore().getState().actions.setRefetchReport(refetch);
};

export const setSummaryStatus = (status: SummaryStatus) => {
  getStore().getState().actions.setSummaryStatus(status);
};

export const setSummaryData = (data: SummaryData | null) => {
  getStore().getState().actions.setSummaryData(data);
};

export const setSummaryErrorMessage = (message: string | null) => {
  getStore().getState().actions.setSummaryErrorMessage(message);
};

export const setIsGeneratingSummary = (reportId: string, isGenerating: boolean) => {
  getStore().getState().actions.setIsGeneratingSummary(reportId, isGenerating);
};